import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useOnboardingFieldsQuery,
  useSetProfileInfoMutation,
} from '@/features/auth/api/useAuthMutations';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useUserDetailKyc } from '@/features/auth/hooks/useUserDetailKyc';
import type {
  FormValues,
  KycDetails,
  OnboardingFieldsResponse,
} from '@/features/auth/types/onboardingFields';
import {
  buildOnboardingFields,
  cleanVerifiedValuesForFields,
  fieldKey,
  findCompanyFieldName,
  getUserDetailStorageKeys,
  hasValue,
  parseJSON,
  syncFormCacheWithApiFields,
} from '@/features/auth/utils/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';

export function useUserDetailPage() {
  const navigate = useNavigate();
  const uploadMutation = useUploadSingleFile();
  const setProfile = useSetProfileInfoMutation();

  const userRole = localStorage.getItem('role');
  const { data, isLoading } = useOnboardingFieldsQuery(userRole);
  const userMobile = localStorage.getItem('user_mobile') || 'temp';

  const { storageKey, verifiedStorageKey, detailsStorageKey } =
    getUserDetailStorageKeys(userMobile);

  const savedDetails = useMemo(
    () => parseJSON<FormValues>(sessionStorage.getItem(storageKey), {}),
    [storageKey],
  );
  const savedVerified = useMemo(
    () =>
      parseJSON<Record<string, string>>(
        sessionStorage.getItem(verifiedStorageKey),
        {},
      ),
    [verifiedStorageKey],
  );
  const savedKycDetails = useMemo(
    () =>
      parseJSON<{
        panDetails?: KycDetails | null;
        gstDetails?: KycDetails | null;
      }>(sessionStorage.getItem(detailsStorageKey), {}),
    [detailsStorageKey],
  );

  const [showModal, setShowModal] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: savedDetails });

  const onboardingData = data as OnboardingFieldsResponse | undefined;
  const selectedFields = onboardingData?.data?.selectedFields || [];
  const kycStatus = onboardingData?.data?.kycStatus;

  const onboardingFields = useMemo(
    () => buildOnboardingFields(selectedFields),
    [selectedFields],
  );

  const apiFieldNames = useMemo(
    () => onboardingFields.map((row) => row.field.fieldName),
    [onboardingFields],
  );

  const fieldSignature = apiFieldNames.slice().sort().join('|');

  const companyFieldName = useMemo(
    () => findCompanyFieldName(selectedFields),
    [selectedFields],
  );

  const kyc = useUserDetailKyc({
    watch,
    setValue,
    kycStatus,
    savedDetails,
    savedVerified,
    savedKycDetails,
    verifiedStorageKey,
    detailsStorageKey,
    companyFieldName,
    onboardingFields,
  });

  useEffect(() => {
    if (isLoading || !data) return;
    if (selectedFields.length > 0 && !fieldSignature) return;

    const next = syncFormCacheWithApiFields(storageKey, apiFieldNames);
    reset({ ...next, confirmAccurate: false, confirmNoGst: false });

    const allowed = new Set([...apiFieldNames, 'mediaId']);
    kyc.setVerifiedValuesFromSync((prev) =>
      cleanVerifiedValuesForFields(verifiedStorageKey, allowed, prev),
    );
    // Only re-sync when the API field set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSignature, isLoading, data]);

  useEffect(() => {
    if (!isLoading && data && onboardingFields.length === 0) {
      navigate('/create-password', { replace: true });
    }
  }, [data, isLoading, navigate, onboardingFields.length]);

  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname);
    const onPopState = () => {
      setShowModal(true);
      window.history.pushState(null, '', window.location.pathname);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const confirmAccurate = Boolean(watch('confirmAccurate'));
  const confirmNoGst = Boolean(watch('confirmNoGst'));

  const gstFieldName = useMemo(
    () =>
      onboardingFields.find((row) => fieldKey(row.field.fieldName) === KYC.GST)
        ?.field.fieldName,
    [onboardingFields],
  );
  const gstValue = gstFieldName ? watch(gstFieldName) : '';
  const isGstVerified = Boolean(
    gstFieldName && kyc.isKycVerified(gstFieldName),
  );
  // Hide after GST is entered and verified; show only while GST is empty.
  const showNoGstDeclaration =
    Boolean(gstFieldName) && !hasValue(gstValue) && !isGstVerified;

  useEffect(() => {
    if (!showNoGstDeclaration) setValue('confirmNoGst', false);
  }, [setValue, showNoGstDeclaration]);

  const canContinue =
    confirmAccurate && (!showNoGstDeclaration || confirmNoGst);

  const handleConfirmLeave = () => {
    setShowModal(false);
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(verifiedStorageKey);
    sessionStorage.removeItem(detailsStorageKey);
    navigate('/signup', { replace: true })
  };

  const onSubmit = (formData: FormValues) => {
    if (!formData.confirmAccurate) {
      toast.error('Please confirm the details before continuing');
      return;
    }
    if (showNoGstDeclaration && !formData.confirmNoGst) {
      toast.error('Please confirm you are not registered under GST');
      return;
    }
    if (kyc.pendingVerification) {
      toast.error(
        kyc.pendingVerifyMessage ||
        'Please verify required fields before submitting',
      );
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify(formData));

    const allowed = new Set([...apiFieldNames, 'mediaId']);
    const cleanedData: Record<string, unknown> = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'confirmAccurate' || key === 'confirmNoGst') return;
      if (!allowed.has(key)) return;
      if (value instanceof FileList) cleanedData[key] = formData.mediaId;
      else if (key !== 'mediaId') cleanedData[key] = value;
    });

    localStorage.removeItem('SESSION_ID_TWO');
    setProfile.mutate({
      ...cleanedData,
      sessionId: localStorage.getItem('SESSION_ID'),
    });
  };

  return {
    isLoading,
    onboardingFields,
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    errors,
    showModal,
    setShowModal,
    handleConfirmLeave,
    onSubmit,
    pendingVerification: kyc.pendingVerification,
    confirmAccurate,
    confirmNoGst,
    canContinue,
    showNoGstDeclaration,
    pendingVerifyMessage: kyc.pendingVerifyMessage,
    isSubmitting: setProfile.isPending,
    shouldShowVerifyButton: kyc.shouldShowVerifyButton,
    isKycField: kyc.isKycField,
    isFieldLocked: kyc.isFieldLocked,
    unlockVerifiedField: kyc.unlockVerifiedField,
    verifyPanNumber: kyc.verifyPanNumber,
    verifyGstNumber: kyc.verifyGstNumber,
    panRows: kyc.panRows,
    gstRows: kyc.gstRows,
    uploadMutation,
    verifyPanPending: kyc.verifyPanPending,
    verifyGstPending: kyc.verifyGstPending,
  };
}
